import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Package, Eye } from 'lucide-react';
import { productsApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import ConfirmModal from '../../components/common/ConfirmModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, product: null, index: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${BACKEND_URL}${imagePath}`;
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsApi.getAll();

      const productsData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.products)
        ? response.data.products
        : Array.isArray(response.data?.cartItems)
        ? response.data.cartItems
        : [];

      setProducts(productsData);
    } catch (err) {
      console.error('Error fetching products:', err);
      toast.error('Məhsulları yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.product) return;

    try {
      setDeleting(true);

      const productId = deleteModal.product._id;
      await productsApi.delete(productId);

      setProducts((prev) => prev.filter((item) => item._id !== productId));
      toast.success('Məhsul uğurla silindi');
      setDeleteModal({ open: false, product: null, index: null });
    } catch (err) {
      console.error('Error deleting product:', err);
      toast.error('Məhsulu silmək mümkün olmadı');
    } finally {
      setDeleting(false);
    }
  };

  const getProductName = (product) => {
    return product.productName || 'Adsız';
  };

  const getProductImage = (product) => {
    return product.mainImg || '';
  };

  const getProductPrice = (product) => {
    return Number(product.productPrice || 0);
  };

  const getProductCategory = (product) => {
    return product.productCategory || 'Kateqoriyasız';
  };

  if (loading) {
    return <LoadingSpinner text="Məhsullar yüklənir..." />;
  }

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl text-brand-brown">Məhsulların idarəsi</h1>
          <p className="text-sm text-brand-muted mt-1">{products.length} məhsul mövcuddur</p>
        </div>

        <Link
          to="/admin/add-product"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-primary to-[#E08E73] text-white px-6 py-3 font-medium hover:shadow-warm transition-all duration-300"
          data-testid="admin-add-product-btn"
        >
          <Plus className="w-5 h-5" />
          Yeni məhsul əlavə et
        </Link>
      </div>

      {products.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 shadow-card">
          <EmptyState
            icon={Package}
            title="Hələ məhsul yoxdur"
            description="Kataloqu doldurmağa başlayın"
          />
          <div className="text-center mt-4">
            <Link
              to="/admin/add-product"
              className="inline-flex items-center gap-2 text-brand-primary hover:underline"
            >
              <Plus className="w-4 h-4" />
              İlk məhsulu əlavə et
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead className="bg-brand-cream/50 border-b border-brand-secondary/30">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Şəkil
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Ad
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Kateqoriya
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Qiymət
                  </th>
                  <th className="text-right px-6 py-4 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-brand-secondary/20">
                {products.map((product, index) => (
                  <tr
                    key={product._id || index}
                    className="hover:bg-brand-accent/5 transition-colors"
                    data-testid={`product-row-${product._id || index}`}
                  >
                    <td className="px-6 py-4">
                      <div className="w-16 h-16 rounded-xl bg-brand-muted/10 overflow-hidden">
                        {getProductImage(product) ? (
                          <img
                            src={getImageUrl(getProductImage(product))}
                            alt={getProductName(product)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-brand-muted" />
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <p className="font-medium text-brand-brown">{getProductName(product)}</p>
                    </td>

                    <td className="px-6 py-4">
                      <span className="text-xs bg-brand-secondary/50 text-brand-brown px-3 py-1 rounded-full">
                        {getProductCategory(product)}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <p className="font-serif text-brand-primary">
                        {getProductPrice(product).toFixed(2)} ₼
                      </p>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/products/${product._id}`}
                          target="_blank"
                          className="p-2 rounded-xl text-brand-muted hover:text-brand-primary hover:bg-brand-accent/20 transition-colors"
                          title="Bax"
                          data-testid={`view-product-${product._id}`}
                        >
                          <Eye className="w-5 h-5" />
                        </Link>

                        <button
                          onClick={() => setDeleteModal({ open: true, product, index })}
                          className="p-2 rounded-xl text-brand-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Sil"
                          data-testid={`delete-product-${product._id || index}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteModal.open}
        onOpenChange={(open) =>
          setDeleteModal({
            open,
            product: open ? deleteModal.product : null,
            index: open ? deleteModal.index : null,
          })
        }
        title="Məhsulu sil"
        description={`"${
          deleteModal.product ? getProductName(deleteModal.product) : ''
        }" məhsulunu silmək istədiyinizə əminsiniz?`}
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
};

export default AdminProducts;